import React, { useState } from "react"
import PropTypes from "prop-types"
import { OldButton } from "@kit/design/old-button"
import { values } from "lodash"
import { useCurrentUser } from "hooks/use-current-user"
import { BillingAddressInput } from "../billing-address-input"
import { Action } from "@kit/design/modal"
import { Tooltip } from "@kit/design/tooltip"

import {
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement
} from "@stripe/react-stripe-js"

export const CardForm = props => {
  const isValidString = (value, max = 150) => {
    if (!value) {
      return false
    }

    // Checking max length is just a precaution against maliciously large values being submitted in the form
    const string = `${value}`
    return !!string && string.length > 0 && string.length <= max
  }

  const elements = useElements()
  const user = useCurrentUser()

  const [billingAddress, setBillingAddress] = useState("")
  const [billingCity, setBillingCity] = useState("")
  const [billingState, setBillingState] = useState("")
  const [billingPostalCode, setBillingPostalCode] = useState("")
  const [billingCountry, setBillingCountry] = useState(user.ip_country || "")
  const [error, setError] = useState(null)
  const [submittable, setSubmittable] = useState(false)

  const [validity, setValidity] = useState({
    address: false,
    country: isValidString(billingCountry),
    city: false,
    state: false,
    postalCode: false,
    cardNumber: false,
    cardCvc: false,
    cardExpiry: false
  })

  const handleSubmit = async event => {
    event.preventDefault()

    if (props.busy || !submittable) {
      return
    }

    setError(null)

    props.onSubmit(elements.getElement(CardNumberElement), {
      address: billingAddress,
      city: billingCity,
      state: billingState,
      postalCode: billingPostalCode,
      country: billingCountry
    })
  }

  const updateError = errorMessage => {
    setError(errorMessage)
    setSubmittable(!errorMessage && !values(validity).includes(false))
  }

  const handleAddressChange = value => {
    setBillingAddress(value)

    const valid = isValidString(value)
    validity.address = valid
    setValidity(validity)

    updateError(valid ? null : "Address is required.")

    props.onChange({ field: "address", value: value })
  }

  const handleCityChange = value => {
    setBillingCity(value)

    const valid = isValidString(value)
    validity.city = valid
    setValidity(validity)

    updateError(valid ? null : "City is required.")

    props.onChange({ field: "city", value: value })
  }

  const handleStateChange = value => {
    setBillingState(value)

    const valid = isValidString(value)
    validity.state = valid
    setValidity(validity)

    updateError(valid ? null : "State is required.")

    props.onChange({ field: "state", value: value })
  }

  const handlePostalCodeChange = value => {
    setBillingPostalCode(value)

    const valid = isValidString(value, 10)
    validity.postalCode = valid
    setValidity(validity)

    updateError(valid ? null : "Postal code is invalid.")

    props.onChange({ field: "postalCode", value: value })
  }

  const handleCountryChange = value => {
    setBillingCountry(value)

    const valid = isValidString(value)
    validity.country = valid
    setValidity(validity)

    updateError(valid ? null : "Country is required.")

    props.onChange({ field: "country", value: value })
  }

  const stripeElementChange = event => {
    if (event.complete) {
      validity[event.elementType] = true
    } else {
      validity[event.elementType] = false
    }

    setValidity(validity)

    const message = event.error?.message

    updateError(message)

    // We cannot access the value from Stripe Elements inputs
    props.onChange({ field: event.elementType, value: null })
  }

  const handleStripeElementChange = props.noElementChange
    ? null
    : stripeElementChange

  const submitButtonText = props.busy
    ? "Working..."
    : props.buttonText || "Submit Card"

  const elementOptions = {
    style: {
      base: {
        "::placeholder": {
          color: "#6e6e6e"
        }
      }
    }
  }

  return (
    <section className="card-form">
      <div className="form-group mb-0">
        <form id="cardForm" onSubmit={handleSubmit}>
          <div className="row mb-4">
            <div className="col-sm-6">
              <label htmlFor="cc-number" className="w-full font-semibold">
                Card Number
                <CardNumberElement
                  id="cc-number"
                  className="kit-input p-3 mt-1"
                  onChange={handleStripeElementChange}
                  options={elementOptions}
                />
              </label>
            </div>

            <div className="col-sm-3">
              <label htmlFor="cc-expiration" className="w-full font-semibold">
                Expiration
                <CardExpiryElement
                  className="kit-input p-3 mt-1"
                  onChange={handleStripeElementChange}
                  options={elementOptions}
                />
              </label>
            </div>

            <div className="col-sm-3">
              <label htmlFor="cc-csc" className="w-full font-semibold">
                Security Code
                <CardCvcElement
                  className="kit-input p-3 mt-1"
                  onChange={handleStripeElementChange}
                  options={elementOptions}
                />
              </label>
            </div>
          </div>

          <div className="row mb-8">
            <div className="col-sm-12">
              <BillingAddressInput
                address={billingAddress}
                city={billingCity}
                state={billingState}
                postalCode={billingPostalCode}
                country={billingCountry}
                onAddressChange={handleAddressChange}
                onCityChange={handleCityChange}
                onStateChange={handleStateChange}
                onPostalCodeChange={handlePostalCodeChange}
                onCountryChange={handleCountryChange}
              />
            </div>
          </div>

          <div className="text-right">
            {props.modalOptions ? (
              <>
                <Action is="secondary" onClick={props.modalOptions.handleClose}>
                  Cancel
                </Action>
                <Action
                  is="primary"
                  form="cardForm"
                  disabled={props.busy || !submittable}
                >
                  {submitButtonText}
                </Action>
              </>
            ) : (
              <PaymentButton
                className="w-full"
                is="primary"
                disabled={props.busy || !submittable}
                config={props.config}
              >
                {submitButtonText}
              </PaymentButton>
            )}
          </div>

          {error && (
            <div className="row mt-4">
              <div className="col-sm-12">
                <div className="errors">{error}</div>
              </div>
            </div>
          )}
        </form>
      </div>
    </section>
  )
}

function PaymentButton({ children, config, disabled }) {
  const useTooltipForDisabled = config?.useTooltipForDisabled
  if (!useTooltipForDisabled || (useTooltipForDisabled && !disabled)) {
    return (
      <OldButton className="w-full" is="primary" size="lg" disabled={disabled}>
        {children}
      </OldButton>
    )
  }

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <OldButton className="w-full" is="primary" size="lg">
            {children}
          </OldButton>
        }
      />
      <Tooltip.Content position="top">
        Fill in your credit card details
      </Tooltip.Content>
    </Tooltip>
  )
}

CardForm.propTypes = {
  buttonText: PropTypes.string,
  busy: PropTypes.bool,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  noElementChange: PropTypes.bool,
  modalOptions: PropTypes.shape({
    handleClose: PropTypes.func
  }),
  config: PropTypes.shape({
    useTooltipForDisabled: PropTypes.bool
  })
}
